import { useState, useEffect } from "react";
import axios from "axios";
import {
  Settings, Mail, Server, RefreshCw, CheckCircle, XCircle,
  Loader2, Plus, Trash2,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const getHeaders = () => {
  const token = localStorage.getItem("token");
  return token ? { Authorization: `Bearer ${token}` } : {};
};

export default function MailSettings() {
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [accountDialog, setAccountDialog] = useState(false);
  const [signature, setSignature] = useState(localStorage.getItem("mail_signature") || "");

  useEffect(() => {
    fetchAccounts();
  }, []);

  const fetchAccounts = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/mail/external-accounts`, {
        headers: getHeaders(), withCredentials: true,
      });
      setAccounts(res.data.external_accounts || []);
    } catch {}
    setLoading(false);
  };

  const handleCreateAccount = async (data) => {
    try {
      await axios.post(`${API}/mail/external-accounts`, data, {
        headers: getHeaders(), withCredentials: true,
      });
      toast.success("External account created");
      setAccountDialog(false);
      fetchAccounts();
    } catch {
      toast.error("Failed to create account");
    }
  };

  const handleDeleteAccount = async (id) => {
    try {
      await axios.delete(`${API}/mail/external-accounts/${id}`, {
        headers: getHeaders(), withCredentials: true,
      });
      toast.success("Account deleted");
      fetchAccounts();
    } catch {
      toast.error("Failed to delete account");
    }
  };

  const handleSync = async (id) => {
    try {
      const res = await axios.post(`${API}/mail/external-accounts/${id}/sync`, {}, {
        headers: getHeaders(), withCredentials: true,
      });
      toast.success(`Sync completed: ${res.data.new_emails || 0} new emails`);
    } catch {
      toast.error("Sync failed");
    }
  };

  const handleTest = async (id) => {
    try {
      const res = await axios.post(`${API}/mail/external-accounts/${id}/test`, {}, {
        headers: getHeaders(), withCredentials: true,
      });
      toast.success(res.data.imap ? "Connection OK" : "Connection failed");
    } catch {
      toast.error("Connection test failed");
    }
  };

  const saveSignature = () => {
    localStorage.setItem("mail_signature", signature);
    toast.success("Signature saved");
  };

  return (
    <div className="p-4 max-w-3xl">
      <Tabs defaultValue="general">
        <TabsList className="rounded-none border border-slate-200 bg-white mb-4">
          <TabsTrigger value="general" className="text-xs rounded-none data-[state=active]:bg-violet-50">
            <Settings className="w-3.5 h-3.5 mr-1" /> General
          </TabsTrigger>
          <TabsTrigger value="accounts" className="text-xs rounded-none data-[state=active]:bg-violet-50">
            <Server className="w-3.5 h-3.5 mr-1" /> External Accounts
          </TabsTrigger>
        </TabsList>

        <TabsContent value="general">
          <div className="bg-white border border-slate-200 p-4 space-y-4">
            <h3 className="text-sm font-semibold text-slate-800">Email Signature</h3>
            <textarea
              value={signature}
              onChange={(e) => setSignature(e.target.value)}
              rows={4}
              className="w-full text-sm px-3 py-2 border border-slate-200 rounded focus:outline-none focus:border-violet-400"
              placeholder="Enter your email signature..."
            />
            <Button size="sm" onClick={saveSignature} className="h-8 text-xs rounded-none bg-violet-600 hover:bg-violet-500">
              Save Signature
            </Button>
          </div>
        </TabsContent>

        <TabsContent value="accounts">
          <div className="flex justify-end mb-3">
            <Dialog open={accountDialog} onOpenChange={setAccountDialog}>
              <DialogTrigger asChild>
                <Button size="sm" className="h-8 text-xs rounded-none bg-violet-600 hover:bg-violet-500">
                  <Plus className="w-3 h-3 mr-1" /> Add Account
                </Button>
              </DialogTrigger>
              <DialogContent className="rounded-none">
                <DialogHeader>
                  <DialogTitle className="text-sm">External Email Account</DialogTitle>
                </DialogHeader>
                <AccountForm onSubmit={handleCreateAccount} onCancel={() => setAccountDialog(false)} />
              </DialogContent>
            </Dialog>
          </div>

          {loading ? (
            <Loader2 className="w-6 h-6 animate-spin text-violet-500 mx-auto py-8" />
          ) : accounts.length === 0 ? (
            <div className="text-center py-8 text-slate-400 text-sm bg-white border border-slate-200">
              <Mail className="w-10 h-10 mx-auto mb-2 text-slate-300" />
              <p>No external accounts configured</p>
              <p className="text-[10px] mt-1">Add an IMAP/SMTP account to sync external email</p>
            </div>
          ) : (
            <div className="space-y-2">
              {accounts.map((acct) => (
                <div key={acct.id} className="bg-white border border-slate-200 p-3 flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-slate-800">{acct.name}</p>
                    <p className="text-xs text-slate-400">{acct.email}</p>
                    <p className="text-[10px] text-slate-400">
                      IMAP: {acct.imap_host}:{acct.imap_port} | SMTP: {acct.smtp_host}:{acct.smtp_port}
                    </p>
                    {acct.last_sync_at && (
                      <p className="text-[10px] text-slate-400 mt-0.5">
                        Last sync: {new Date(acct.last_sync_at).toLocaleString("en-ZA")}
                      </p>
                    )}
                  </div>
                  <div className="flex items-center gap-1">
                    <Button variant="ghost" size="sm" onClick={() => handleTest(acct.id)}
                      className="h-6 text-[10px] px-2 gap-1">
                      <CheckCircle className="w-3 h-3 text-green-500" /> Test
                    </Button>
                    <Button variant="ghost" size="sm" onClick={() => handleSync(acct.id)}
                      className="h-6 text-[10px] px-2 gap-1">
                      <RefreshCw className="w-3 h-3" /> Sync
                    </Button>
                    <Button variant="ghost" size="sm" onClick={() => handleDeleteAccount(acct.id)}
                      className="h-6 text-[10px] px-2 text-red-500">
                      <Trash2 className="w-3 h-3" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}

function AccountForm({ onSubmit, onCancel }) {
  const [data, setData] = useState({
    name: "", email: "", username: "", password: "",
    imap_host: "", imap_port: 993, smtp_host: "", smtp_port: 465,
    sync_interval_minutes: 15,
  });

  return (
    <div className="space-y-3 max-h-[400px] overflow-auto">
      <div className="grid grid-cols-2 gap-2">
        <Input placeholder="Account name" value={data.name} onChange={(e) => setData({ ...data, name: e.target.value })}
          className="h-8 text-xs rounded-none" />
        <Input placeholder="Email address" value={data.email} onChange={(e) => setData({ ...data, email: e.target.value })}
          className="h-8 text-xs rounded-none" />
      </div>
      <div className="grid grid-cols-2 gap-2">
        <Input placeholder="Username" value={data.username} onChange={(e) => setData({ ...data, username: e.target.value })}
          className="h-8 text-xs rounded-none" />
        <Input type="password" placeholder="Password" value={data.password} onChange={(e) => setData({ ...data, password: e.target.value })}
          className="h-8 text-xs rounded-none" />
      </div>
      <h4 className="text-[10px] font-semibold text-slate-400 uppercase">IMAP Settings</h4>
      <div className="grid grid-cols-3 gap-2">
        <Input placeholder="IMAP host" value={data.imap_host} onChange={(e) => setData({ ...data, imap_host: e.target.value })}
          className="h-8 text-xs rounded-none" />
        <Input type="number" placeholder="Port" value={data.imap_port} onChange={(e) => setData({ ...data, imap_port: parseInt(e.target.value) || 993 })}
          className="h-8 text-xs rounded-none" />
      </div>
      <h4 className="text-[10px] font-semibold text-slate-400 uppercase">SMTP Settings</h4>
      <div className="grid grid-cols-3 gap-2">
        <Input placeholder="SMTP host" value={data.smtp_host} onChange={(e) => setData({ ...data, smtp_host: e.target.value })}
          className="h-8 text-xs rounded-none" />
        <Input type="number" placeholder="Port" value={data.smtp_port} onChange={(e) => setData({ ...data, smtp_port: parseInt(e.target.value) || 465 })}
          className="h-8 text-xs rounded-none" />
      </div>
      <div className="flex justify-end gap-2 pt-2">
        <Button variant="outline" size="sm" onClick={onCancel} className="h-8 text-xs rounded-none">Cancel</Button>
        <Button size="sm" onClick={() => onSubmit(data)} className="h-8 text-xs rounded-none bg-violet-600" disabled={!data.name.trim() || !data.email.trim()}>Save</Button>
      </div>
    </div>
  );
}
